/**
 * useSheetData Hook
 * Story 3.3: Central data hook for sheet operations
 * Story 4.1: Added grouping support with buildGroupedRows
 * Story 4.3: Added date range filter support
 * Story 4.4: Added transpose support with mutual exclusion from grouping
 * Story 6.3: Added HyperFormula integration for formula evaluation
 *
 * Architecture Decisions:
 * - D3: View Architecture - Headless data layer for all views
 * - D4: State Management - React Query for server state, Zustand for UI state
 * - D5: API Design - Uses REST endpoints from Stories 3.1 and 3.2
 * - D6: Formula Engine - HyperFormula for reactive recalculation
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSheetUIStore, type ActiveCell, toast } from '../stores';
import * as api from '../api/sheets';
import type { Sheet } from '../types/types/sheet';
import type { Field, FieldType, FieldConfig } from '../types/types/field';
import type { Row, RowData } from '../types/types/row';
import type { CellValue } from '../types/types/cell';
import type { GroupingConfig } from '../types/types/view';
import { buildGroupedRows, getExpandedGroupIds, getVisibleRows } from '../views/grid/utils/grouping';
import type { GridColumn, GridRow, DateRangeFilter, TransposeConfig } from '../views/grid/types/gridTypes';
import { isDateInRange, dateRangeToFilterCondition } from '../views/grid/utils/dateRangeFilter';
import { buildTransposedData, transposeResultToGrid } from '../views/grid/utils/transpose';
import { formulaEngineManager, USE_HYPERFORMULA, type EvaluationResult } from '../services';

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Map FieldType to GridColumn type
 * Field uses 'multi_select', Grid uses 'multi-select'
 * Defined at module level to be available for mutation callbacks
 */
type ColumnType = 'text' | 'number' | 'currency' | 'percent' | 'date' | 'checkbox' | 'select' | 'multi-select' | 'rating' | 'formula';

function mapFieldTypeToColumnType(fieldType: FieldType): ColumnType {
  if (fieldType === 'multi_select') return 'multi-select';
  const directMappedTypes: ColumnType[] = [
    'text', 'number', 'currency', 'percent', 'date', 'checkbox', 'select', 'rating', 'formula'
  ];
  if (directMappedTypes.includes(fieldType as ColumnType)) {
    return fieldType as ColumnType;
  }
  return 'text';
}

// =============================================================================
// TYPES
// =============================================================================

/**
 * NewField - Input type for creating a new field
 */
export interface NewField {
  name: string;
  type: FieldType;
  config?: FieldConfig;
  width?: number;
}

/**
 * UseSheetDataReturn - Hook return type matching architecture spec
 * AC-3.7: Returns sheet, fields, rows, and mutation functions
 * Story 4.1: Added grouping support
 * Story 4.4: Added transpose support
 */
export interface UseSheetDataReturn {
  // Data (AC-3.19)
  sheet: Sheet | undefined;
  fields: Field[];
  rows: Row[];
  isLoading: boolean;
  error: Error | null;
  /** Refetch all data (Story 6.1 - refresh support) */
  refetch: () => Promise<unknown>;

  // Grouped data (Story 4.1)
  groupedRows: GridRow[];
  visibleRows: GridRow[];
  columns: GridColumn[];

  // Transposed data (Story 4.4)
  transposedColumns: GridColumn[];
  transposedRows: GridRow[];
  isTransposed: boolean;

  // Row operations (AC-3.21, AC-3.22, AC-3.23)
  addRow: (data?: RowData) => Promise<Row>;
  updateRow: (rowId: string, data: Partial<RowData>) => Promise<Row>;
  deleteRow: (rowId: string) => Promise<void>;

  // Field operations (AC-3.24, AC-3.25, AC-3.26)
  addField: (field: NewField) => Promise<Field>;
  updateField: (fieldId: string, updates: Partial<Field>) => Promise<Field>;
  deleteField: (fieldId: string, confirm?: boolean) => Promise<void>;
  reorderFields: (fieldIds: string[]) => Promise<void>;

  // Cell operations (AC-3.27)
  updateCell: (rowId: string, fieldId: string, value: CellValue) => Promise<void>;

  // UI State from Zustand (AC-3.28, AC-3.29)
  selectedRows: string[];
  setSelectedRows: (ids: string[]) => void;
  activeCell: ActiveCell | null;
  setActiveCell: (cell: ActiveCell | null) => void;

  // Grouping state (Story 4.1)
  groupingConfig: GroupingConfig | null;
  setGroupingConfig: (config: GroupingConfig | null) => void;
  expandedGroupIds: string[];
  resolvedExpandedGroupIds: string[];
  setGroupExpanded: (groupId: string, expanded: boolean) => void;
  toggleGroupExpanded: (groupId: string) => void;
  collapseAllGroups: () => void;
  expandAllGroups: () => void;

  // Date range filter state (Story 4.3)
  dateRangeFilter: DateRangeFilter | null;
  setDateRangeFilter: (filter: DateRangeFilter | null) => void;

  // Transpose state (Story 4.4)
  transposeConfig: TransposeConfig | null;
  setTransposeConfig: (config: TransposeConfig | null) => void;

  // Formula Engine (Story 6.3)
  /** Whether HyperFormula is enabled for this sheet */
  formulaEngineEnabled: boolean;
  /** Evaluate a formula for a specific cell */
  evaluateFormula: (rowId: string, fieldId: string, formula: string) => EvaluationResult;
  /** Get computed value for a formula cell */
  getFormulaValue: (rowId: string, fieldId: string) => unknown;
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const queryKeys = {
  sheet: (sheetId: string) => ['sheet', sheetId] as const,
  rows: (sheetId: string) => ['rows', sheetId] as const,
  fields: (sheetId: string) => ['fields', sheetId] as const,
};

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * useSheetData - Headless data layer hook for sheet operations
 *
 * @param sheetId - Sheet ID to load and manage
 * @returns Sheet data, mutations, and UI state
 */
export function useSheetData(sheetId: string): UseSheetDataReturn {
  const queryClient = useQueryClient();

  // ---------------------------------------------------------------------------
  // UI State from Zustand Store (AC-3.28, AC-3.29)
  // ---------------------------------------------------------------------------
  const selectedRows = useSheetUIStore((state) => state.selectedRows);
  const setSelectedRows = useSheetUIStore((state) => state.setSelectedRows);
  const activeCell = useSheetUIStore((state) => state.activeCell);
  const setActiveCell = useSheetUIStore((state) => state.setActiveCell);

  // ---------------------------------------------------------------------------
  // Grouping State from Zustand Store (Story 4.1)
  // ---------------------------------------------------------------------------
  const groupingConfig = useSheetUIStore((state) => state.groupingConfig);
  const setGroupingConfig = useSheetUIStore((state) => state.setGroupingConfig);
  const expandedGroupIds = useSheetUIStore((state) => state.expandedGroupIds);
  const toggleGroupExpanded = useSheetUIStore((state) => state.toggleGroupExpanded);
  const collapseAllGroups = useSheetUIStore((state) => state.collapseAllGroups);
  const storeExpandAllGroups = useSheetUIStore((state) => state.expandAllGroups);
  const expansionInitializedRef = useRef(false);
  const lastGroupingConfigKeyRef = useRef<string | null>(null);

  // ---------------------------------------------------------------------------
  // Date Range Filter State from Zustand Store (Story 4.3)
  // ---------------------------------------------------------------------------
  const dateRangeFilter = useSheetUIStore((state) => state.dateRangeFilter);
  const setDateRangeFilter = useSheetUIStore((state) => state.setDateRangeFilter);

  // ---------------------------------------------------------------------------
  // Transpose State from Zustand Store (Story 4.4)
  // ---------------------------------------------------------------------------
  const transposeConfig = useSheetUIStore((state) => state.transposeConfig);
  const setTransposeConfig = useSheetUIStore((state) => state.setTransposeConfig);

  // Reset expansion initialization when grouping config changes
  const groupingConfigKey = useMemo(() => {
    if (!groupingConfig) return 'none';
    return JSON.stringify({
      enabled: groupingConfig.enabled,
      fields: groupingConfig.fields,
      defaultExpanded: groupingConfig.defaultExpanded,
    });
  }, [groupingConfig]);

  if (lastGroupingConfigKeyRef.current !== groupingConfigKey) {
    expansionInitializedRef.current = false;
    lastGroupingConfigKeyRef.current = groupingConfigKey;
  }

  // ---------------------------------------------------------------------------
  // Data Queries (AC-3.19, AC-3.20)
  // ---------------------------------------------------------------------------

  /**
   * Sheet query - fetches sheet metadata
   */
  const sheetQuery = useQuery({
    queryKey: queryKeys.sheet(sheetId),
    queryFn: () => api.fetchSheet(sheetId),
    enabled: !!sheetId,
  });

  /**
   * Rows query - fetches all rows for the sheet
   */
  const rowsQuery = useQuery({
    queryKey: queryKeys.rows(sheetId),
    queryFn: async () => {
      const response = await api.fetchRows(sheetId);
      return response.rows;
    },
    enabled: !!sheetId,
  });

  /**
   * Fields query - fetches all fields for the sheet
   */
  const fieldsQuery = useQuery({
    queryKey: queryKeys.fields(sheetId),
    queryFn: async () => {
      const response = await api.fetchFields(sheetId);
      return response.fields;
    },
    enabled: !!sheetId,
  });

  // ---------------------------------------------------------------------------
  // Row Mutations with Optimistic Updates (AC-3.21, AC-3.22, AC-3.23)
  // ---------------------------------------------------------------------------

  /**
   * Add Row Mutation
   * AC-3.21: Creates row via POST API and updates cache optimistically
   */
  const addRowMutation = useMutation({
    mutationFn: (data: RowData = {}) => api.createRow(sheetId, data),
    onMutate: async (data) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.rows(sheetId) });

      // Snapshot previous value
      const previousRows = queryClient.getQueryData<Row[]>(queryKeys.rows(sheetId));

      // Create optimistic row with temp ID
      const tempId = `temp-${Date.now()}`;
      const optimisticRow: Row = {
        id: tempId,
        sheetId,
        data: data || {},
        position: (previousRows?.length ?? 0),
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Add to cache
      queryClient.setQueryData<Row[]>(queryKeys.rows(sheetId), (old) => [
        ...(old || []),
        optimisticRow,
      ]);

      return { previousRows, tempId };
    },
    onSuccess: (newRow, _data, context) => {
      // Replace temp row with real row
      queryClient.setQueryData<Row[]>(queryKeys.rows(sheetId), (old) =>
        old?.map((row) => (row.id === context?.tempId ? newRow : row)) ?? [newRow]
      );
    },
    onError: (_error, _data, context) => {
      // Rollback on error (AC-3.12)
      if (context?.previousRows) {
        queryClient.setQueryData(queryKeys.rows(sheetId), context.previousRows);
      }
      toast.error('Failed to add row');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.rows(sheetId) });
    },
  });

  /**
   * Update Row Mutation
   * AC-3.22: Patches row via PATCH API with optimistic update
   */
  const updateRowMutation = useMutation({
    mutationFn: ({ rowId, data }: { rowId: string; data: Partial<RowData> }) =>
      api.updateRow(rowId, data),
    onMutate: async ({ rowId, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.rows(sheetId) });

      // Snapshot previous value
      const previousRows = queryClient.getQueryData<Row[]>(queryKeys.rows(sheetId));

      // Optimistically update cache
      queryClient.setQueryData<Row[]>(queryKeys.rows(sheetId), (old) =>
        old?.map((row) =>
          row.id === rowId
            ? { ...row, data: { ...row.data, ...data } as RowData, updatedAt: new Date() }
            : row
        )
      );

      return { previousRows };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error (AC-3.12)
      if (context?.previousRows) {
        queryClient.setQueryData(queryKeys.rows(sheetId), context.previousRows);
      }
      toast.error('Failed to update row');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rows(sheetId) });
    },
  });

  /**
   * Delete Row Mutation
   * AC-3.23: Soft-deletes row via DELETE API with optimistic update
   */
  const deleteRowMutation = useMutation({
    mutationFn: (rowId: string) => api.deleteRow(rowId),
    onMutate: async (rowId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.rows(sheetId) });

      // Snapshot previous value
      const previousRows = queryClient.getQueryData<Row[]>(queryKeys.rows(sheetId));

      // Optimistically remove from cache
      queryClient.setQueryData<Row[]>(queryKeys.rows(sheetId), (old) =>
        old?.filter((row) => row.id !== rowId)
      );

      return { previousRows };
    },
    onError: (_error, _rowId, context) => {
      // Rollback on error (AC-3.12)
      if (context?.previousRows) {
        queryClient.setQueryData(queryKeys.rows(sheetId), context.previousRows);
      }
      toast.error('Failed to delete row');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rows(sheetId) });
    },
  });

  // ---------------------------------------------------------------------------
  // Field Mutations with Optimistic Updates (AC-3.24, AC-3.25, AC-3.26)
  // ---------------------------------------------------------------------------

  /**
   * Add Field Mutation
   * AC-3.24: Creates field via POST API with cache update
   */
  const addFieldMutation = useMutation({
    mutationFn: (field: NewField) => api.createField(sheetId, field),
    onMutate: async (field) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.fields(sheetId) });
      const previousFields = queryClient.getQueryData<Field[]>(queryKeys.fields(sheetId));

      // Optimistic field
      const tempId = `temp-field-${Date.now()}`;
      const optimisticField: Field = {
        id: tempId,
        sheetId,
        name: field.name,
        type: field.type,
        config: field.config ?? {},
        width: field.width ?? 150,
        position: previousFields?.length ?? 0,
        locked: false,
      };

      queryClient.setQueryData<Field[]>(queryKeys.fields(sheetId), (old) => [
        ...(old || []),
        optimisticField,
      ]);

      return { previousFields, tempId };
    },
    onSuccess: (newField, _data, context) => {
      queryClient.setQueryData<Field[]>(queryKeys.fields(sheetId), (old) =>
        old?.map((f) => (f.id === context?.tempId ? newField : f)) ?? [newField]
      );
    },
    onError: (_error, _data, context) => {
      if (context?.previousFields) {
        queryClient.setQueryData(queryKeys.fields(sheetId), context.previousFields);
      }
      toast.error('Failed to add field');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fields(sheetId) });
    },
  });

  /**
   * Update Field Mutation
   * AC-3.25: Patches field via PATCH API with optimistic update
   * Story 6.3: Also updates formula engine column mapping immediately for renames
   */
  const updateFieldMutation = useMutation({
    mutationFn: ({ fieldId, updates }: { fieldId: string; updates: Partial<Field> }) =>
      api.updateField(fieldId, updates),
    onMutate: async ({ fieldId, updates }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.fields(sheetId) });
      const previousFields = queryClient.getQueryData<Field[]>(queryKeys.fields(sheetId));

      // Optimistically update the cache
      const updatedFields = previousFields?.map((f) => (f.id === fieldId ? { ...f, ...updates } : f));
      queryClient.setQueryData<Field[]>(queryKeys.fields(sheetId), updatedFields);

      // Story 6.3: Immediately update formula engine column mapping for renames
      // This ensures formulas can resolve [NewName] right away
      if (USE_HYPERFORMULA && updatedFields && updates.name) {
        const updatedColumns = updatedFields.map((field): GridColumn => ({
          id: field.id,
          label: field.name,
          type: mapFieldTypeToColumnType(field.type),
          width: field.width || 150,
          options: field.config?.options?.map((opt: string | { value: string }) =>
            typeof opt === 'string' ? opt : opt.value
          ),
          readOnly: field.locked,
          isPrimary: field.position === 0,
        }));
        formulaEngineManager.updateColumnMapping(sheetId, updatedColumns);
      }

      return { previousFields };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousFields) {
        queryClient.setQueryData(queryKeys.fields(sheetId), context.previousFields);
        // Revert formula engine column mapping
        if (USE_HYPERFORMULA) {
          const revertedColumns = context.previousFields.map((field): GridColumn => ({
            id: field.id,
            label: field.name,
            type: mapFieldTypeToColumnType(field.type),
            width: field.width || 150,
            options: field.config?.options?.map((opt: string | { value: string }) =>
              typeof opt === 'string' ? opt : opt.value
            ),
            readOnly: field.locked,
            isPrimary: field.position === 0,
          }));
          formulaEngineManager.updateColumnMapping(sheetId, revertedColumns);
        }
      }
      toast.error('Failed to update field');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fields(sheetId) });
    },
  });

  /**
   * Delete Field Mutation
   * AC-3.26: Deletes field via DELETE API; requires confirm if field has data
   */
  const deleteFieldMutation = useMutation({
    mutationFn: ({ fieldId, confirm }: { fieldId: string; confirm?: boolean }) =>
      api.deleteField(fieldId, confirm),
    onMutate: async ({ fieldId }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.fields(sheetId) });
      const previousFields = queryClient.getQueryData<Field[]>(queryKeys.fields(sheetId));

      queryClient.setQueryData<Field[]>(queryKeys.fields(sheetId), (old) =>
        old?.filter((f) => f.id !== fieldId)
      );

      return { previousFields };
    },
    onError: (error, _variables, context) => {
      if (context?.previousFields) {
        queryClient.setQueryData(queryKeys.fields(sheetId), context.previousFields);
      }
      // Check if it's a confirmation required error
      if (error instanceof api.ApiError && error.code === 'CONFIRMATION_REQUIRED') {
        toast.error('Field has data. Confirm deletion to proceed.');
      } else {
        toast.error('Failed to delete field');
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fields(sheetId) });
    },
  });

  /**
   * Reorder Fields Mutation
   * Reorders fields by updating positions
   */
  const reorderFieldsMutation = useMutation({
    mutationFn: (fieldIds: string[]) => api.reorderFields(sheetId, fieldIds),
    onMutate: async (fieldIds) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.fields(sheetId) });
      const previousFields = queryClient.getQueryData<Field[]>(queryKeys.fields(sheetId));

      // Optimistically reorder fields
      if (previousFields) {
        const fieldMap = new Map(previousFields.map((f) => [f.id, f]));
        const reorderedFields = fieldIds
          .map((id, index) => {
            const field = fieldMap.get(id);
            return field ? { ...field, position: index } : null;
          })
          .filter((f): f is Field => f !== null);

        queryClient.setQueryData<Field[]>(queryKeys.fields(sheetId), reorderedFields);
      }

      return { previousFields };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousFields) {
        queryClient.setQueryData(queryKeys.fields(sheetId), context.previousFields);
      }
      toast.error('Failed to reorder fields');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fields(sheetId) });
    },
  });

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  // Combined loading state (AC-3.20)
  const isLoading = sheetQuery.isLoading || rowsQuery.isLoading || fieldsQuery.isLoading;

  // Combined error state (AC-3.20)
  const error = sheetQuery.error || rowsQuery.error || fieldsQuery.error;

  // ---------------------------------------------------------------------------
  // Grouping Computation (Story 4.1 - AC-4.1, AC-4.2, AC-4.3)
  // ---------------------------------------------------------------------------

  /**
   * Memoized columns derived from fields
   * Story 6.4: Include formulaExpression for formula-type columns
   */
  const columns = useMemo((): GridColumn[] => {
    const fields = fieldsQuery.data ?? [];
    return fields.map((field): GridColumn => ({
      id: field.id,
      label: field.name,
      type: mapFieldTypeToColumnType(field.type),
      width: field.width || 150,
      options: field.config?.options?.map((opt: string | { value: string }) =>
        typeof opt === 'string' ? opt : opt.value
      ),
      readOnly: field.locked,
      isPrimary: field.position === 0,
      // Story 6.4: Include formula expression for formula columns
      formulaExpression: field.type === 'formula' && typeof field.config?.formula === 'string'
        ? field.config.formula
        : undefined,
      // Story 6.4: Include semantic type for formula result formatting
      semanticType: field.config?.semanticType as GridColumn['semanticType'],
      // Story 6.4: Include number format for decimal precision
      numberFormat: field.config?.numberFormat as GridColumn['numberFormat'],
    }));
  }, [fieldsQuery.data]);

  /**
   * Memoized rows converted to GridRow format
   * Story 6.4: Automatically compute formula column values using HyperFormula
   * AC-6.4.2: Formula cells display calculated values
   * AC-6.4.6: Live preview updates immediately when dependencies change
   */
  const gridRows = useMemo((): GridRow[] => {
    const rows = rowsQuery.data ?? [];
    const formulaColumns = columns.filter(col => col.type === 'formula' && col.formulaExpression);

    return rows.map((row, rowIndex): GridRow => {
      const baseValues = row.data as Record<string, unknown>;

      // If no formula columns or HyperFormula not available, return base values
      if (formulaColumns.length === 0 || !USE_HYPERFORMULA) {
        return {
          id: row.id,
          values: baseValues,
          rowType: 'data',
          attachments: row.attachments || [],
          attachmentCount: row.attachments?.length || 0,
        };
      }

      // Compute formula column values
      const computedValues: Record<string, unknown> = { ...baseValues };

      for (const col of formulaColumns) {
        if (!col.formulaExpression) continue;

        const result = formulaEngineManager.evaluateFormula(
          sheetId,
          col.formulaExpression,
          rowIndex,
          col.id,
          rows.map((r): GridRow => ({
            id: r.id,
            values: r.data as Record<string, unknown>,
            rowType: 'data',
          }))
        );

        if (result.error) {
          // Store error code as value for display
          computedValues[col.id] = result.error.code;
        } else {
          computedValues[col.id] = result.value;
        }
      }

      return {
        id: row.id,
        values: computedValues,
        rowType: 'data',
        attachments: row.attachments || [],
        attachmentCount: row.attachments?.length || 0,
      };
    });
  }, [rowsQuery.data, columns, sheetId]);

  /**
   * Memoized filtered rows with date range filter applied (Story 4.3)
   * AC-4.3.2: Only rows with date values within range are shown
   * AC-4.3.3: Date range filter combines with other filters using AND logic
   */
  const filteredGridRows = useMemo((): GridRow[] => {
    if (!dateRangeFilter) {
      return gridRows;
    }

    const condition = dateRangeToFilterCondition(dateRangeFilter);
    if (!condition) {
      return gridRows;
    }

    return gridRows.filter(row => {
      const value = row.values[condition.columnId];
      return isDateInRange(
        value as Date | string | null,
        condition.value[0] || null,
        condition.value[1] || null
      );
    });
  }, [gridRows, dateRangeFilter]);

  /**
   * Memoized grouped rows (Story 4.1 - AC-4.1, AC-4.2)
   * Applies grouping via buildGroupedRows when groupingConfig.enabled
   * Uses JSON.stringify to avoid object reference issues in deps
   * Story 4.3: Now uses filteredGridRows to respect date range filter
   */
  const groupedRows = useMemo((): GridRow[] => {
    if (!groupingConfig?.enabled || groupingConfig.fields.length === 0) {
      return filteredGridRows;
    }
    return buildGroupedRows(filteredGridRows, columns, groupingConfig);
  }, [filteredGridRows, columns, JSON.stringify(groupingConfig)]);

  /**
   * Initialize expansion state when grouping defaults to expanded.
   * Runs once per grouping configuration to avoid clobbering user toggles.
   */
  useEffect(() => {
    if (!groupingConfig?.enabled) return;

    const hasGroupRows = groupedRows.some(row => row.rowType === 'group');
    if (!hasGroupRows) return;

    if (!expansionInitializedRef.current) {
      if (groupingConfig.defaultExpanded && expandedGroupIds.length === 0) {
        const allGroupIds = groupedRows
          .filter(row => row.rowType === 'group')
          .map(row => row.id);

        if (allGroupIds.length > 0) {
          storeExpandAllGroups(allGroupIds);
        }
      }

      expansionInitializedRef.current = true;
    }
  }, [groupingConfig, groupedRows, expandedGroupIds.length, storeExpandAllGroups]);

  /**
   * Memoized visible rows (respects expand/collapse state)
   * Converts expandedGroupIds array to Set for getVisibleRows function.
   * When defaultExpanded is true and no explicit state exists yet, derive the
   * expanded set from the grouping config so child rows render by default.
   */
  const effectiveExpandedSet = useMemo(() => {
    if (!groupingConfig?.enabled) return null;

    if (expansionInitializedRef.current || expandedGroupIds.length > 0) {
      return new Set(expandedGroupIds);
    }

    return groupingConfig ? getExpandedGroupIds(groupedRows, groupingConfig) : new Set<string>();
  }, [expandedGroupIds, groupedRows, groupingConfig]);

  const visibleRows = useMemo((): GridRow[] => {
    if (!groupingConfig?.enabled) {
      // Story 4.3: Return filtered rows when grouping is disabled
      return filteredGridRows;
    }
    const expandedSet = effectiveExpandedSet ?? new Set<string>();
    return getVisibleRows(groupedRows, expandedSet);
  }, [filteredGridRows, groupedRows, effectiveExpandedSet, groupingConfig?.enabled]);

  const resolvedExpandedGroupIds = useMemo(
    () => (groupingConfig?.enabled ? Array.from(effectiveExpandedSet ?? []) : []),
    [effectiveExpandedSet, groupingConfig?.enabled]
  );

  // ---------------------------------------------------------------------------
  // Transpose Computation (Story 4.4 - AC-4.4.1, AC-4.4.2)
  // ---------------------------------------------------------------------------

  /**
   * Check if transpose is active (AC-4.4.7: mutually exclusive with grouping)
   */
  const isTransposed = useMemo(() => {
    return transposeConfig?.enabled === true && !groupingConfig?.enabled;
  }, [transposeConfig?.enabled, groupingConfig?.enabled]);

  /**
   * Memoized transposed data (Story 4.4 - AC-4.4.1, AC-4.4.2)
   * Transforms flat rows into pivot-table structure when transpose is enabled
   */
  const transposeResult = useMemo(() => {
    if (!isTransposed || !transposeConfig) {
      return null;
    }

    // Get time slicer config from grouping config if available on the column field
    const columnGroupingField = groupingConfig?.fields.find(
      f => f.fieldId === transposeConfig.columnFieldId
    );
    const timeSlicer = columnGroupingField?.timeSlicer;

    return buildTransposedData(filteredGridRows, columns, transposeConfig, timeSlicer);
  }, [isTransposed, transposeConfig, filteredGridRows, columns, groupingConfig?.fields]);

  /**
   * Memoized transposed columns and rows for grid display
   */
  const { transposedColumns, transposedRows } = useMemo((): { transposedColumns: GridColumn[]; transposedRows: GridRow[] } => {
    if (!transposeResult || !transposeConfig) {
      return { transposedColumns: [], transposedRows: [] };
    }

    // Get row field label for header
    const rowField = columns.find(c => c.id === transposeConfig.rowFieldId);
    const rowFieldLabel = rowField?.label ?? 'Row';

    const result = transposeResultToGrid(transposeResult, rowFieldLabel);
    return { transposedColumns: result.columns, transposedRows: result.rows };
  }, [transposeResult, transposeConfig, columns]);

  /**
   * Expand all groups - collects all group IDs and expands them
   */
  const expandAllGroups = (): void => {
    const allGroupIds = groupedRows
      .filter(row => row.rowType === 'group')
      .map(row => row.id);
    storeExpandAllGroups(allGroupIds);
  };

  /**
   * Explicitly set the expansion state for a single group ID.
   * Uses the resolved expansion set so we behave correctly before Zustand
   * has been seeded with default expansion state.
   */
  const setGroupExpanded = useCallback(
    (groupId: string, expanded: boolean): void => {
      const next = new Set(resolvedExpandedGroupIds);

      if (expanded) {
        next.add(groupId);
      } else {
        next.delete(groupId);
      }

      storeExpandAllGroups(Array.from(next));
    },
    [resolvedExpandedGroupIds, storeExpandAllGroups]
  );

  // Wrapper functions for cleaner API

  const addRow = async (data: RowData = {}): Promise<Row> => {
    return addRowMutation.mutateAsync(data);
  };

  const updateRow = async (rowId: string, data: Partial<RowData>): Promise<Row> => {
    return updateRowMutation.mutateAsync({ rowId, data });
  };

  const deleteRow = async (rowId: string): Promise<void> => {
    await deleteRowMutation.mutateAsync(rowId);
  };

  const addField = async (field: NewField): Promise<Field> => {
    return addFieldMutation.mutateAsync(field);
  };

  /**
   * Update a field and cascade formula updates if the field name changed
   * AC-6.3.4: Column renames automatically update formulas
   */
  const updateField = async (fieldId: string, updates: Partial<Field>): Promise<Field> => {
    const currentFields = fieldsQuery.data ?? [];
    const targetField = currentFields.find((f) => f.id === fieldId);

    // Check if this is a rename operation
    const isRename = targetField && updates.name && updates.name !== targetField.name;
    const oldName = isRename ? targetField.name : null;
    const newName = isRename ? updates.name : null;

    // STEP 1: Update the column name FIRST so the formula engine mapping is correct
    const result = await updateFieldMutation.mutateAsync({ fieldId, updates });

    // STEP 2: After the column is renamed, update any formulas that reference it
    if (isRename && oldName && newName) {
      // Find all fields with formulas that reference the old column name
      const fieldsWithFormulas = currentFields.filter(
        (f) => f.config?.formula && typeof f.config.formula === 'string'
      );

      // Update formulas in other fields that reference this column
      const formulaUpdatePromises: Promise<Field>[] = [];
      for (const field of fieldsWithFormulas) {
        const formula = field.config?.formula as string;
        // Match [OldName] pattern (case-insensitive)
        const regex = new RegExp(`\\[${escapeRegExp(oldName)}\\]`, 'gi');
        if (regex.test(formula)) {
          const updatedFormula = formula.replace(regex, `[${newName}]`);
          formulaUpdatePromises.push(
            updateFieldMutation.mutateAsync({
              fieldId: field.id,
              updates: {
                config: { ...field.config, formula: updatedFormula },
              },
            })
          );
        }
      }

      // Execute all formula updates in parallel
      if (formulaUpdatePromises.length > 0) {
        await Promise.all(formulaUpdatePromises);
      }
    }

    return result;
  };

  /**
   * Escape special regex characters in a string
   */
  function escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  const deleteField = async (fieldId: string, confirm?: boolean): Promise<void> => {
    await deleteFieldMutation.mutateAsync({ fieldId, confirm });
  };

  const reorderFields = async (fieldIds: string[]): Promise<void> => {
    await reorderFieldsMutation.mutateAsync(fieldIds);
  };

  /**
   * updateCell - Convenience wrapper for updateRow with single cell change
   * AC-3.27: Updates a single cell value by calling updateRow
   */
  const updateCell = async (
    rowId: string,
    fieldId: string,
    value: CellValue
  ): Promise<void> => {
    await updateRow(rowId, { [fieldId]: value });
  };

  // Refetch function to refresh all data (Story 6.1 - refresh support)
  const refetch = useCallback(() => {
    return Promise.all([
      sheetQuery.refetch(),
      rowsQuery.refetch(),
      fieldsQuery.refetch(),
    ]);
  }, [sheetQuery, rowsQuery, fieldsQuery]);

  // ---------------------------------------------------------------------------
  // Formula Engine Integration (Story 6.3 - AC-6.3.8)
  // ---------------------------------------------------------------------------

  /**
   * Track if formula engine has been initialized for this sheet
   */
  const formulaEngineInitializedRef = useRef(false);

  /**
   * Track previous row state for incremental updates
   */
  const prevGridRowsRef = useRef<GridRow[]>([]);

  /**
   * Initialize the formula engine once when sheet data first loads
   * AC-6.3.7: Sheet instance management with column definitions and row data sync
   */
  useEffect(() => {
    if (!USE_HYPERFORMULA || !sheetId || !columns.length || gridRows.length === 0) {
      return;
    }

    // Only initialize once - subsequent changes use incremental updates
    if (!formulaEngineInitializedRef.current) {
      formulaEngineManager.initializeSheet(sheetId, columns, gridRows);
      formulaEngineInitializedRef.current = true;
      prevGridRowsRef.current = gridRows;
    }

    // Cleanup on unmount
    return () => {
      if (formulaEngineInitializedRef.current) {
        formulaEngineManager.destroyEngine(sheetId);
        formulaEngineInitializedRef.current = false;
        prevGridRowsRef.current = [];
      }
    };
  // Only run on initial mount and sheetId change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId]);

  /**
   * Apply incremental updates when gridRows change after initialization
   * AC-6.3.8: Reactive recalculation via incremental cell updates
   */
  useEffect(() => {
    if (!USE_HYPERFORMULA || !sheetId || !formulaEngineInitializedRef.current) {
      return;
    }

    const prevRows = prevGridRowsRef.current;
    const currentRows = gridRows;

    // Skip if no previous data (initialization handles this)
    if (prevRows.length === 0) {
      prevGridRowsRef.current = currentRows;
      return;
    }

    // Detect changes and apply incrementally
    const prevRowMap = new Map(prevRows.map((r) => [r.id, r]));
    const currentRowMap = new Map(currentRows.map((r) => [r.id, r]));

    // Use batch mode for multiple updates
    formulaEngineManager.batch(sheetId, () => {
      // Find updated or new rows
      currentRows.forEach((row, idx) => {
        const prevRow = prevRowMap.get(row.id);
        if (!prevRow) {
          // New row - add to engine
          formulaEngineManager.addRow(sheetId, columns, row);
        } else if (JSON.stringify(row.values) !== JSON.stringify(prevRow.values)) {
          // Updated row - update in engine
          formulaEngineManager.updateRow(sheetId, idx, columns, row);
        }
      });

      // Find deleted rows
      prevRows.forEach((prevRow, idx) => {
        if (!currentRowMap.has(prevRow.id)) {
          // Row deleted - remove from engine
          formulaEngineManager.removeRow(sheetId, idx);
        }
      });
    });

    prevGridRowsRef.current = currentRows;
  }, [sheetId, gridRows, columns]);

  /**
   * Update column mappings when columns change (for column renames)
   * AC-6.3.4: Column renames automatically update formulas
   */
  useEffect(() => {
    if (!USE_HYPERFORMULA || !sheetId || !formulaEngineInitializedRef.current) {
      return;
    }
    formulaEngineManager.updateColumnMapping(sheetId, columns);
  }, [sheetId, columns]);

  /**
   * Evaluate a formula for a specific cell
   * AC-6.3.3: Excel function support
   * AC-6.3.4: Bracket notation support
   * AC-6.3.5: Cell notation support
   */
  const evaluateFormula = useCallback(
    (rowId: string, fieldId: string, formula: string): EvaluationResult => {
      if (!USE_HYPERFORMULA || !formulaEngineInitializedRef.current) {
        // Fallback: return the formula as-is when engine not available
        return { value: formula };
      }

      // Find row index
      const rowIndex = gridRows.findIndex((r) => r.id === rowId);
      if (rowIndex === -1) {
        return { value: null, error: { code: '#REF!', message: 'Row not found' } };
      }

      return formulaEngineManager.evaluateFormula(
        sheetId,
        formula,
        rowIndex,
        fieldId,
        gridRows
      );
    },
    [sheetId, gridRows]
  );

  /**
   * Get the computed value for a formula cell
   * AC-6.3.8: Reactive recalculation - get current computed values
   */
  const getFormulaValue = useCallback(
    (rowId: string, fieldId: string): unknown => {
      if (!USE_HYPERFORMULA || !formulaEngineInitializedRef.current) {
        return null;
      }

      // Find row index
      const rowIndex = gridRows.findIndex((r) => r.id === rowId);
      if (rowIndex === -1) {
        return null;
      }

      return formulaEngineManager.getCellValue(sheetId, rowIndex, fieldId);
    },
    [sheetId, gridRows]
  );

  return {
    // Data
    sheet: sheetQuery.data,
    fields: fieldsQuery.data ?? [],
    rows: rowsQuery.data ?? [],
    isLoading,
    error: error as Error | null,
    refetch,

    // Grouped data (Story 4.1)
    groupedRows,
    visibleRows,
    columns,

    // Transposed data (Story 4.4)
    transposedColumns,
    transposedRows,
    isTransposed,

    // Row operations
    addRow,
    updateRow,
    deleteRow,

    // Field operations
    addField,
    updateField,
    deleteField,
    reorderFields,

    // Cell operations
    updateCell,

    // UI State
    selectedRows,
    setSelectedRows,
    activeCell,
    setActiveCell,

    // Grouping state (Story 4.1)
    groupingConfig,
    setGroupingConfig,
    expandedGroupIds,
    resolvedExpandedGroupIds,
    setGroupExpanded,
    toggleGroupExpanded,
    collapseAllGroups,
    expandAllGroups,

    // Date range filter state (Story 4.3)
    dateRangeFilter,
    setDateRangeFilter,

    // Transpose state (Story 4.4)
    transposeConfig,
    setTransposeConfig,

    // Formula Engine (Story 6.3)
    formulaEngineEnabled: USE_HYPERFORMULA,
    evaluateFormula,
    getFormulaValue,
  };
}

export default useSheetData;
